import api from './api';
import type { User, Business, LoginCredentials, SignupData, UserRole, Permission } from '../types';

interface ApiUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  role: string;
  roleName?: string;
  roleDisplayName?: string;
  roleId?: string;
  companyId: string;
  isSuperAdmin?: boolean;
  permissions?: string[];
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  user: ApiUser;
  business?: Business;
}

interface ValidateResponse {
  user: ApiUser;
  business: Business;
  permissions?: Permission[];
}

// Map API role to client-expected role
const mapRole = (apiRole: string): UserRole => {
  const roleMap: Record<string, UserRole> = {
    'SUPERADMIN': 'admin',
    'admin': 'admin',
    'manager': 'manager',
    'customer_service': 'customer_service',
    'viewer': 'viewer',
  };
  return roleMap[apiRole] || 'viewer';
};

// Transform API user to client User type
const transformUser = (apiUser: ApiUser): User => ({
  id: apiUser.id,
  firstName: apiUser.firstName,
  lastName: apiUser.lastName,
  email: apiUser.email,
  phone: apiUser.phoneNumber,
  role: mapRole(apiUser.role || apiUser.roleName || 'viewer'),
  roleId: apiUser.roleId,
  roleDisplayName: apiUser.roleDisplayName,
  status: 'active' as const,
  businessId: apiUser.companyId,
  isSuperAdmin: apiUser.isSuperAdmin || false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Transform API business to client Business type
const transformBusiness = (apiBusiness: any): Business => ({
  id: apiBusiness.id,
  name: apiBusiness.name,
  address: apiBusiness.address || '',
  city: apiBusiness.city || '',
  state: apiBusiness.state || '',
  zipCode: apiBusiness.zipCode || '',
  phone: apiBusiness.phoneNumber || '',
  industry: apiBusiness.industry || '',
  logo: apiBusiness.logo,
  createdAt: apiBusiness.createdAt || new Date().toISOString(),
  updatedAt: apiBusiness.updatedAt || new Date().toISOString(),
});

export const authService = {
  async login(credentials: LoginCredentials): Promise<{ token: string; user: User; business: Business | null; permissions: Permission[] }> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);

    // Store refresh token separately for token refresh functionality
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    const user = transformUser(response.data.user);

    // Sysadmin users have no company association
    let business: Business | null = null;
    if (response.data.business) {
      business = transformBusiness(response.data.business);
    } else if (!user.isSuperAdmin && response.data.user.companyId) {
      business = { id: response.data.user.companyId, name: '', address: '', city: '', state: '', zipCode: '', phone: '', industry: '', createdAt: '', updatedAt: '' };
    }

    return {
      token: response.data.accessToken,
      user,
      business,
      permissions: (response.data.user.permissions || []) as Permission[],
    };
  },

  async signup(data: SignupData): Promise<{ token: string; user: User; business: Business; permissions: Permission[] }> {
    const response = await api.post<AuthResponse>('/auth/signup', data);

    // Store refresh token separately for token refresh functionality
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    return {
      token: response.data.accessToken,
      user: transformUser(response.data.user),
      business: response.data.business
        ? transformBusiness(response.data.business)
        : { id: response.data.user.companyId, name: data.business.name, address: data.business.address, city: data.business.city, state: data.business.state, zipCode: data.business.zipCode, phone: data.business.phone, industry: data.business.industry, createdAt: '', updatedAt: '' },
      permissions: (response.data.user.permissions || []) as Permission[],
    };
  },

  async validateSession(): Promise<{ user: User; business: Business | null; permissions: Permission[] }> {
    const response = await api.get<ValidateResponse>('/auth/me');
    const user = transformUser(response.data.user);
    return {
      user,
      business: response.data.business ? transformBusiness(response.data.business) : null,
      permissions: (response.data.permissions || response.data.user.permissions || []) as Permission[],
    };
  },

  async refreshToken(refreshTokenValue?: string): Promise<{ token: string; refreshToken: string; user: User; permissions: Permission[] }> {
    const tokenToUse = refreshTokenValue || localStorage.getItem('refresh_token');
    if (!tokenToUse) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<{ accessToken: string; refreshToken: string; user: ApiUser }>('/auth/refresh', {
      refreshToken: tokenToUse,
    });

    // Update stored refresh token
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    return {
      token: response.data.accessToken,
      refreshToken: response.data.refreshToken,
      user: transformUser(response.data.user),
      permissions: (response.data.user.permissions || []) as Permission[],
    };
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  async validateResetToken(token: string): Promise<{ valid: boolean }> {
    const response = await api.get<{ valid: boolean }>('/auth/validate-reset-token', { params: { token } });
    return response.data;
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  },

  async validateInvite(token: string): Promise<{ valid: boolean; invitation?: { email: string; companyName: string; roleName?: string } }> {
    const response = await api.get<{ valid: boolean; invitation?: { email: string; companyName: string; roleName?: string } }>('/auth/validate-invite', { params: { token } });
    return response.data;
  },

  async acceptInvite(data: { token: string; firstName: string; lastName: string; password: string; phoneNumber?: string }): Promise<{ token: string; user: User; business: Business; permissions: Permission[] }> {
    const response = await api.post<AuthResponse>('/auth/accept-invite', data);

    // Store refresh token
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    return {
      token: response.data.accessToken,
      user: transformUser(response.data.user),
      business: response.data.business
        ? transformBusiness(response.data.business)
        : { id: response.data.user.companyId, name: '', address: '', city: '', state: '', zipCode: '', phone: '', industry: '', createdAt: '', updatedAt: '' },
      permissions: (response.data.user.permissions || []) as Permission[],
    };
  },
  
  // Clean up on logout
  clearTokens(): void {
    localStorage.removeItem('refresh_token');
  },
};
